import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Users,
  Building2,
  Sparkles,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import PublicHeader from "@/components/common/PublicHeader";
import { api } from "@/api/client";
import StrukturPnbpTree from "@/components/public/StrukturPnbpTree";

// ── Backend types ────────────────────────────────────────────────────────────
type OverviewStats = {
  total_dokumen: number;
  tim_aktif: number;
};

// Indonesian-style number formatter (1.234)
function formatID(n: number): string {
  return n.toLocaleString("id-ID");
}

// ─────────────────────────────────────────────────────────────────────────────
// Landing page — PNBP DMS
// ─────────────────────────────────────────────────────────────────────────────

const PRIMARY = "#1B5189";
const SECONDARY = "#4864E2";

type Stat = {
  icon: React.ReactNode;
  value: string;
  label: string;
  hint: string;
};

function buildStats(overview: OverviewStats | undefined): Stat[] {
  return [
    {
      icon: <FileText className="h-5 w-5" />,
      value: overview ? formatID(overview.total_dokumen) : "5.000+",
      label: "Dokumen Tersimpan",
      hint: "+12% dari bulan lalu",
    },
    {
      icon: <Users className="h-5 w-5" />,
      value: overview ? formatID(overview.tim_aktif) : "250+",
      label: "Tim Aktif",
      hint: "10 kategori PNBP",
    },
    {
      icon: <Building2 className="h-5 w-5" />,
      value: "15+",
      label: "Stakeholder",
      hint: "Lintas kementerian",
    },
    {
      icon: <Sparkles className="h-5 w-5" />,
      value: "98%",
      label: "Akurasi AI",
      hint: "+12% dari bulan lalu",
    },
  ];
}

// ─────────────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const { data: overview } = useQuery<OverviewStats>({
    queryKey: ["public", "stats", "overview"],
    queryFn: () => api.get<OverviewStats>("/public/stats/overview"),
    staleTime: 5 * 60 * 1000,
  });

  const stats = buildStats(overview);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <PublicHeader />

      {/* ─── Hero ───────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 pt-12">
        <div
          className="relative overflow-hidden rounded-3xl bg-slate-900 px-8 py-20 text-center shadow-xl"
          style={{
            backgroundImage: "url('/bg-landing.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="pointer-events-none absolute inset-0 bg-slate-900/30" />

          <div className="relative z-10 mx-auto max-w-4xl">
            {/* <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-medium text-cyan-300 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
              Knowledge Management System v2.0
            </span> */}

            <h1 className="mt-6 text-5xl font-bold leading-tight text-white sm:text-6xl">
              Arsitektur Informasi
              <br />
              PNBP
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Platform informasi menyeluruh tentang Penerimaan Negara Bukan
              Pajak yang memuat informasi tata kelola PNBP dari berbagai sumber
              dalam satu ekosistem terpadu
            </p>
          </div>
        </div>
      </section>

      {/* ─── Struktur PNBP ──────────────────────────────────────── */}
      <section className="py-10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            {/* HIDDEN: heading + description hidden for now. Restore by flipping false → true. */}
            {false && (
              <>
                <h2 className="mt-3 text-4xl font-bold text-slate-900">
                  Struktur PNBP
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600">
                  10 kategori utama struktur pengetahuan PNBP berdasarkan UU No.
                  9 Tahun 2018.
                </p>
              </>
            )}
            <div>
              <Link
                to="/taksonomi"
                className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: "#1B5189" }}
              >
                Lihat Selengkapnya
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* HIDDEN: topic list hidden on landing page for now. Restore by flipping false → true. (excludeIds still drops Topic 10 when shown.) */}
          {false && (
            <div className="mt-12">
              <StrukturPnbpTree excludeIds={[10]} />
            </div>
          )}
        </div>
      </section>

      {/* ─── Stats ──────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                {s.icon}
              </div>
              <div className="mt-4 text-3xl font-bold text-slate-900">
                {s.value}
              </div>
              <div className="mt-1 text-sm font-medium text-slate-700">
                {s.label}
              </div>
              <div className="mt-1 text-xs text-slate-500">{s.hint}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CTA ────────────────────────────────────────────────── */}
      {/* HIDDEN: CTA section hidden for now. Restore by flipping false → true. */}
      {false && (
        <section className="mx-auto max-w-7xl px-6 pb-20">
          <div
            className="rounded-3xl px-8 py-16 text-center shadow-xl"
            style={{ backgroundColor: PRIMARY }}
          >
            <h2 className="text-4xl font-bold leading-tight text-white">
              Siap Mengelola Dokumen PNBP
              <br />
              dengan Lebih Efisien?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-blue-100">
              Bergabunglah dengan tim DJA lainnya dan rasakan kemudahan
              pengelolaan dokumen dengan sistem AI-powered yang modern dan
              terintegrasi.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold shadow-sm transition hover:bg-slate-100"
                style={{ color: PRIMARY }}
              >
                Mulai Sekarang
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="mailto:dja@kemenkeu.go.id"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: SECONDARY }}
              >
                Hubungi Tim
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ─── Footer ─────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <FileText className="h-5 w-5" />
                </div>
                <div className="leading-tight">
                  <div
                    className="text-base font-bold"
                    style={{ color: PRIMARY }}
                  >
                    AI - PNBP
                  </div>
                  <div className="text-xs text-slate-500">
                    Arsitektur Informasi PNBP
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                Arsitektur Informasi PNBP terpadu untuk Direktorat Jenderal
                Anggaran, Kementerian Keuangan Republik Indonesia.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-900">Platform</h4>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                {/* <li>
                  <Link to="/features" className="hover:text-blue-600">
                    Fitur
                  </Link>
                </li> */}
                <li>
                  <Link to="/taksonomi" className="hover:text-blue-600">
                    Taksonomi
                  </Link>
                </li>
                <li>
                  <Link to="/pnbp" className="hover:text-blue-600">
                    Kategori PNBP
                  </Link>
                </li>
                {/* <li>
                  <a href="#" className="hover:text-blue-600">
                    Dokumentasi
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-600">
                    API
                  </a>
                </li> */}
              </ul>
            </div>

            {/* <div>
              <h4 className="text-sm font-bold text-slate-900">Resources</h4>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                <li>
                  <a href="#" className="hover:text-blue-600">
                    Panduan Pengguna
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-600">
                    Training
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-600">
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-blue-600">
                    Support
                  </a>
                </li>
              </ul>
            </div> */}

            <div>
              <h4 className="text-sm font-bold text-slate-900">Contact</h4>
              <ul className="mt-4 space-y-3 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  dja@kemenkeu.go.id
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  +62-xxxxxxxxxx
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  Jakarta, Indonesia
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-slate-200 pt-6 text-xs text-slate-500 md:flex-row">
            <div>© 2026 Direktorat Jenderal Anggaran. All rights reserved.</div>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="hover:text-blue-600">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-blue-600">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
